import { useState, useRef, useEffect, useMemo } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import styles from './VariablePickerPanel.module.css';
import EllipsisTooltip from '@/components/EllipsisTooltip';
import type { FieldGroup, FieldItem } from '@/apis/logiblocksAPI';
import { WarningIcon } from '@/assets/icons/icons';
import { CATEGORY_ICON_BG } from '../types';
import { getVariableTypeLabel } from './variableTypeLabels';

export type { FieldItem };

export interface CustomVariablePickerProps {
  groups: FieldGroup[];
  /** Called with the publishing node id and field name of the chosen variable. */
  onSelect: (nodeId: string, variableName: string, item: FieldItem) => void;
  /** Called on Escape or Backspace-on-empty-search. */
  onDismiss: () => void;
  /** Auto-focus the search input on mount (default: true) */
  autoFocus?: boolean;
}

/**
 * Tells one row apart from another in the list. Two fields read off one
 * meta-variable share a field_name, so the derived name is part of the key,
 * and a node output and a user variable can share a name, so the source is
 * too.
 */
function fieldItemKey(item: FieldItem): string {
  return `${item.field_name}.${item.derived_field_name ?? ''}.${item.source}`;
}

/** The name a row is searched by: "shipment_id.status" for a derived field. */
function fieldItemSearchName(item: FieldItem): string {
  return `${item.field_name}.${item.derived_field_name ?? ''}`;
}

/**
 * Searchable, keyboard-navigable grouped list of API-driven field items.
 * Renders a group header (group_name) then items beneath each group.
 * Fires onSelect(nodeId, field_name) with the same signature as VariablePickerPanel.
 */
export function CustomVariablePicker({
  groups,
  onSelect,
  onDismiss,
  autoFocus = true,
}: CustomVariablePickerProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Flatten all items across groups for search + keyboard nav.
  const allItems = useMemo(
    () =>
      groups.flatMap((g) => g.items.map((item) => ({ ...item, groupInfo: g }))),
    [groups]
  );

  const filtered = useMemo(() => {
    if (!query) return allItems;
    const q = query.toLowerCase();
    return allItems.filter(
      (item) =>
        fieldItemSearchName(item).toLowerCase().includes(q) ||
        item.display_name.toLowerCase().includes(q)
    );
  }, [allItems, query]);

  // Keep cursor in bounds when the list shrinks.
  useEffect(() => {
    setActiveIndex(0);
  }, [filtered]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[activeIndex];
      if (item) {
        const { groupInfo, ...fieldItem } = item;
        onSelect(groupInfo.node_id, item.field_name, fieldItem);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    } else if (e.key === 'Backspace' && query === '') {
      e.preventDefault();
      onDismiss();
    }
  };

  // When searching, render flat filtered list. Otherwise render grouped.
  const renderGrouped = !query;

  return (
    <div className={styles.panel}>
      <div className={styles.search}>
        <SearchOutlined className={styles.searchIcon} />
        <input
          ref={inputRef}
          placeholder='Search...'
          type='text'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.list}>
        {filtered.length === 0 ? (
          <div className={styles.empty}>No variables found</div>
        ) : renderGrouped ? (
          groups.map((group, index) => {
            if (group.items.length === 0) return null;
            return (
              <div key={group.name}>
                {/* Group header */}
                <div className={styles.groupHeader}>
                  <span>{index + 1}.</span>
                  <div
                    className={'lb-icon-wrapper'}
                    style={{
                      backgroundColor:
                        CATEGORY_ICON_BG[
                          group.color as keyof typeof CATEGORY_ICON_BG
                        ] || 'var(--light-gray)',
                    }}
                  >
                    <WarningIcon fill={group.color} width={14} height={14} />
                  </div>
                  <span className='font-12 fw-600'>{group.group_name}</span>
                </div>
                <hr className={styles.divider} />
                <div className={styles.groupItemsInner}>
                  {group.items.map((item) => {
                    const flatIndex = allItems.findIndex(
                      (fi) =>
                        fieldItemKey(fi) === fieldItemKey(item) &&
                        fi.groupInfo.name === group.name
                    );
                    return (
                      <div
                        key={fieldItemKey(item)}
                        className={styles.itemWrapper}
                      >
                        <div
                          className={`${styles.item} ${flatIndex === activeIndex ? styles.itemActive : ''}`}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onSelect(group.node_id, item.field_name, item);
                          }}
                        >
                          <EllipsisTooltip
                            text={item.display_name}
                            className={`${styles.itemLabel} font-12`}
                          />
                          <div className={styles.exampleGroup}>
                            {getVariableTypeLabel(item.type) && (
                              <span className={styles.typeBadge}>
                                {getVariableTypeLabel(item.type)}
                              </span>
                            )}
                            {item.example_string && (
                              <EllipsisTooltip
                                text={item.example_string}
                                className={styles.exampleText}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          filtered.map((item, index) => (
            <div
              key={`${item.groupInfo.name}-${fieldItemKey(item)}`}
              className={styles.itemWrapper}
            >
              <div
                className={`${styles.item} ${index === activeIndex ? styles.itemActive : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  const { groupInfo, ...fieldItem } = item;
                  onSelect(groupInfo.node_id, item.field_name, fieldItem);
                }}
              >
                <EllipsisTooltip
                  text={item.display_name}
                  className={styles.itemLabel}
                />
                <div className={styles.exampleGroup}>
                  {getVariableTypeLabel(item.type) && (
                    <span className={styles.typeBadge}>
                      {getVariableTypeLabel(item.type)}
                    </span>
                  )}
                  {item.example_string && (
                    <EllipsisTooltip
                      text={item.example_string}
                      className={styles.exampleText}
                    />
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
